import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion, Reorder } from "framer-motion";
import { 
  Plus, GripVertical, Edit2, Trash2, Save,
  Package, Star, Zap, Award, Gift, Crown, Check, X, Percent
} from "lucide-react";

const iconOptions = [
  { value: "package", icon: Package, label: "Package" },
  { value: "star", icon: Star, label: "Star" },
  { value: "zap", icon: Zap, label: "Zap" },
  { value: "award", icon: Award, label: "Award" },
  { value: "gift", icon: Gift, label: "Gift" },
  { value: "crown", icon: Crown, label: "Crown" }
];

const getIcon = (iconName) => {
  const found = iconOptions.find(i => i.value === iconName);
  return found ? found.icon : Package;
};

export default function PackagesPage() {
  const { token } = useAuth();
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyPackage = {
    name_en: "",
    name_de: "",
    description_en: "",
    description_de: "",
    info_video_url: "",
    addon_ids: [],
    package_price: 0,
    eligible_plans: ["A", "B", "C", "D"],
    icon: "package",
    badge_text_en: "",
    badge_text_de: "",
    sort_order: packages.length,
    status: "active"
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [packagesRes, addonsRes] = await Promise.all([
        axios.get(`${API}/packages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/addons`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPackages(packagesRes.data);
      setAddons(addonsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (newOrder) => {
    setPackages(newOrder);
    try {
      await axios.post(`${API}/packages/reorder`, 
        newOrder.map(p => p.id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error("Failed to save order");
      fetchData();
    }
  };

  const handleSavePackage = async () => {
    if (!editingPackage.name_en) {
      toast.error("Package name is required");
      return;
    }

    if (editingPackage.addon_ids.length === 0) {
      toast.error("Please select at least one add-on");
      return;
    }

    setSaving(true);
    try {
      if (editingPackage.id) {
        await axios.put(`${API}/packages/${editingPackage.id}`, editingPackage, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Package updated");
      } else {
        await axios.post(`${API}/packages`, editingPackage, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Package created");
      }
      setShowEditor(false);
      setEditingPackage(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save package");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await axios.delete(`${API}/packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Package deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete package");
    }
  };

  const toggleAddonInPackage = (addonId) => {
    setEditingPackage(prev => {
      const currentIds = prev.addon_ids || [];
      const hasAddon = currentIds.includes(addonId);
      return {
        ...prev,
        addon_ids: hasAddon 
          ? currentIds.filter(id => id !== addonId)
          : [...currentIds, addonId]
      };
    });
  };

  const togglePlanEligibility = (plan) => {
    setEditingPackage(prev => {
      const currentPlans = prev.eligible_plans || [];
      const hasPlan = currentPlans.includes(plan);
      return {
        ...prev,
        eligible_plans: hasPlan 
          ? currentPlans.filter(p => p !== plan)
          : [...currentPlans, plan]
      };
    });
  };

  // Calculate original price from selected add-ons
  const calculateOriginalPrice = () => {
    if (!editingPackage) return 0;
    return addons
      .filter(a => editingPackage.addon_ids.includes(a.id))
      .reduce((sum, a) => sum + a.price, 0);
  };

  const originalPrice = calculateOriginalPrice();
  const savings = originalPrice - (editingPackage?.package_price || 0);
  const savingsPercent = originalPrice > 0 ? ((savings / originalPrice) * 100).toFixed(0) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  // Package Editor View
  if (showEditor && editingPackage) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => { setShowEditor(false); setEditingPackage(null); }}
              className="p-2.5 hover:bg-white/5 rounded-xl transition-colors text-white/50 hover:text-white border border-white/10"
            >
              <X className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-display text-2xl lg:text-3xl text-white">
                {editingPackage.id ? "Edit Package" : "New Package"}
              </h1>
              {editingPackage.name_en && (
                <p className="text-white/40 text-sm mt-0.5">{editingPackage.name_en}</p>
              )}
            </div>
          </div>

          <button
            onClick={handleSavePackage}
            disabled={saving}
            className="btn-accent flex items-center gap-2 px-5 py-2.5"
            data-testid="save-package-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Package"}
          </button>
        </div>

          {/* Basic Info */}
          <div className="glass-card-static p-5 lg:p-6">
            <h2 className="font-display text-base text-white mb-4">Package Info</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group mb-0">
                <label className="form-label">Name (EN) *</label>
                <input
                  type="text"
                  value={editingPackage.name_en}
                  onChange={(e) => setEditingPackage(prev => ({ ...prev, name_en: e.target.value }))}
                  className="form-input"
                  placeholder="e.g., Award Package"
                  data-testid="package-name-en"
                />
              </div>
              <div className="form-group mb-0">
                <label className="form-label">Name (DE)</label>
                <input
                  type="text"
                  value={editingPackage.name_de}
                  onChange={(e) => setEditingPackage(prev => ({ ...prev, name_de: e.target.value }))}
                  className="form-input"
                  placeholder="z.B. Award-Paket"
                />
              </div>
              <div className="form-group mb-0">
                <label className="form-label">Description (EN)</label>
                <textarea
                  value={editingPackage.description_en}
                  onChange={(e) => setEditingPackage(prev => ({ ...prev, description_en: e.target.value }))}
                  className="form-input"
                  rows={2}
                  placeholder="Package description..."
                />
              </div>
              <div className="form-group mb-0">
                <label className="form-label">Description (DE)</label>
                <textarea
                  value={editingPackage.description_de}
                  onChange={(e) => setEditingPackage(prev => ({ ...prev, description_de: e.target.value }))}
                  className="form-input"
                  rows={2}
                  placeholder="Paketbeschreibung..."
                />
              </div>
            </div>

            <div className="form-group mb-0 mt-4">
              <label className="form-label">Info Video URL</label>
              <input
                type="url"
                value={editingPackage.info_video_url || ""}
                onChange={(e) => setEditingPackage(prev => ({ ...prev, info_video_url: e.target.value }))}
                className="form-input"
                placeholder="https://youtube.com/watch?v=... (optional)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="form-group mb-0">
                <label className="form-label">Icon</label>
                <div className="flex gap-2">
                  {iconOptions.map(opt => {
                    const IconComp = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setEditingPackage(prev => ({ ...prev, icon: opt.value }))}
                        className={`p-3 rounded-xl transition-colors ${
                          editingPackage.icon === opt.value 
                            ? "bg-[#C9A96E]/20 text-[#C9A96E]" 
                            : "bg-white/5 text-white/40 hover:bg-white/10"
                        }`}
                        title={opt.label}
                      >
                        <IconComp className="w-5 h-5" />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="form-group mb-0">
                <label className="form-label">Badge (EN)</label>
                <input
                  type="text"
                  value={editingPackage.badge_text_en}
                  onChange={(e) => setEditingPackage(prev => ({ ...prev, badge_text_en: e.target.value }))}
                  className="form-input"
                  placeholder="e.g., BEST VALUE"
                />
              </div>
              <div className="form-group mb-0">
                <label className="form-label">Badge (DE)</label>
                <input
                  type="text"
                  value={editingPackage.badge_text_de}
                  onChange={(e) => setEditingPackage(prev => ({ ...prev, badge_text_de: e.target.value }))}
                  className="form-input"
                  placeholder="z.B. BESTER WERT"
                />
              </div>
            </div>
          </div>

          {/* Add-ons Selection */}
          <div className="glass-card-static p-6">
            <h2 className="font-display text-lg text-white mb-4">Select Add-ons for this Package</h2>
            <p className="text-white/40 text-sm mb-4">Click to add/remove add-ons from this package</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {addons.map(addon => {
                const isSelected = editingPackage.addon_ids.includes(addon.id);
                return (
                  <button
                    key={addon.id}
                    onClick={() => toggleAddonInPackage(addon.id)}
                    className={`p-4 rounded-xl text-left transition-all ${
                      isSelected 
                        ? "bg-[#C9A96E]/20 border-2 border-[#C9A96E]" 
                        : "bg-white/5 border-2 border-transparent hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-white">{addon.name_en}</h4>
                        <p className="text-white/40 text-sm mt-1">{addon.description_en}</p>
                      </div>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        isSelected ? "bg-[#C9A96E]" : "bg-white/10"
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                    <div className="mt-3 text-[#C9A96E] font-mono text-lg">
                      €{addon.price}
                    </div>
                  </button>
                );
              })}
            </div>

            {addons.length === 0 && (
              <div className="text-center py-8 text-white/40">
                No add-ons available. <Link to="/addons" className="text-[#C9A96E]">Create some first</Link>
              </div>
            )}
          </div>

          {/* Pricing */}
          <div className="glass-card-static p-6">
            <h2 className="font-display text-lg text-white mb-4">Package Pricing</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Original Price */}
              <div className="text-center p-4 bg-white/5 rounded-xl">
                <p className="text-white/40 text-sm mb-2">Original Price</p>
                <p className="text-2xl font-mono text-white/60 line-through">
                  €{originalPrice}
                </p>
                <p className="text-white/30 text-xs mt-1">
                  ({editingPackage.addon_ids.length} add-ons)
                </p>
              </div>

              {/* Package Price */}
              <div className="text-center p-4 bg-[#C9A96E]/10 rounded-xl border-2 border-[#C9A96E]">
                <p className="text-white/60 text-sm mb-2">Package Price *</p>
                <div className="flex items-center justify-center gap-1">
                  <span className="text-white/60">€</span>
                  <input
                    type="number"
                    value={editingPackage.package_price}
                    onChange={(e) => setEditingPackage(prev => ({ 
                      ...prev, 
                      package_price: parseFloat(e.target.value) || 0 
                    }))}
                    className="w-24 bg-transparent border-none text-center font-mono text-3xl text-[#C9A96E] focus:outline-none"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Savings */}
              <div className="text-center p-4 bg-emerald-500/10 rounded-xl">
                <p className="text-white/40 text-sm mb-2">Customer Saves</p>
                <p className="text-2xl font-mono text-emerald-400">
                  €{savings > 0 ? savings : 0}
                </p>
                {savingsPercent > 0 && (
                  <p className="text-emerald-400/60 text-sm mt-1 flex items-center justify-center gap-1">
                    <Percent className="w-4 h-4" />
                    {savingsPercent}% off
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Plan Eligibility */}
          <div className="glass-card-static p-6">
            <h2 className="font-display text-lg text-white mb-4">Available for Plans</h2>
            <div className="flex gap-3">
              {["A", "B", "C", "D"].map(plan => {
                const planNames = { A: "Starter", B: "Professional", C: "Business", D: "Enterprise" };
                const isEligible = editingPackage.eligible_plans.includes(plan);
                return (
                  <button
                    key={plan}
                    onClick={() => togglePlanEligibility(plan)}
                    className={`px-4 py-3 rounded-xl transition-colors flex-1 ${
                      isEligible 
                        ? "bg-[#C9A96E]/20 text-[#C9A96E] border-2 border-[#C9A96E]" 
                        : "bg-white/5 text-white/40 border-2 border-transparent hover:bg-white/10"
                    }`}
                  >
                    <div className="text-center">
                      <span className="block font-medium">{planNames[plan]}</span>
                      <span className="text-xs opacity-60">Plan {plan}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
      </div>
    );
  }

  // Package List View
  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-white mb-1" data-testid="packages-title">Add-on Packages</h1>
          <p className="text-white/40 text-sm">Bundle add-ons for better deals</p>
        </div>

        <button
          onClick={() => { setEditingPackage(emptyPackage); setShowEditor(true); }}
          className="btn-accent flex items-center gap-2 px-5 py-2.5"
          data-testid="create-package-btn"
        >
          <Plus className="w-4 h-4" />
          New Package
        </button>
      </div>

      <div className="max-w-5xl">
        {packages.length === 0 ? (
          <div className="glass-card-static p-12 text-center">
            <Package className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="font-display text-xl text-white mb-2">No Packages Yet</h3>
            <p className="text-white/40 mb-6">Create your first package to bundle add-ons with discounted pricing</p>
            <button
              onClick={() => { setEditingPackage(emptyPackage); setShowEditor(true); }}
              className="btn-accent inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Package
            </button>
          </div>
        ) : (
          <Reorder.Group axis="y" values={packages} onReorder={handleReorder} className="space-y-3">
            {packages.map((pkg) => {
              const IconComp = getIcon(pkg.icon);
              return (
                <Reorder.Item
                  key={pkg.id}
                  value={pkg}
                  className="glass-card-static p-4 cursor-grab active:cursor-grabbing"
                  data-testid={`package-${pkg.id}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Drag Handle */}
                    <div className="p-2 text-white/30 hover:text-white/60 transition-colors">
                      <GripVertical className="w-5 h-5" />
                    </div>

                    {/* Icon */}
                    <div className="w-12 h-12 rounded-xl bg-[#C9A96E]/20 flex items-center justify-center">
                      <IconComp className="w-6 h-6 text-[#C9A96E]" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-display text-lg text-white">{pkg.name_en}</h3>
                        {pkg.badge_text_en && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                            {pkg.badge_text_en}
                          </span>
                        )}
                      </div>
                      <p className="text-white/40 text-sm truncate">{pkg.description_en || "No description"}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-white/50">
                          {pkg.addon_ids?.length || 0} add-ons
                        </span>
                        <span className="text-white/20">•</span>
                        <span className="text-white/40 line-through text-sm">€{pkg.original_price}</span>
                        <span className="text-[#C9A96E] font-mono font-bold">€{pkg.package_price}</span>
                        {pkg.savings_percent > 0 && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                            Save {pkg.savings_percent}%
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingPackage(pkg); setShowEditor(true); }}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/40 hover:text-white"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeletePackage(pkg.id); }}
                        className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-white/40 hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
        )}
      </div>
    </div>
  );
}
